/**
 * Where the comment-thread popover goes on screen, given the pin it belongs to.
 *
 * UI-57 follow-up: the old placement put the popover 100px above the pin and
 * only reacted to the BOTTOM edge — a pin near the top of the viewport got a
 * negative `top`, so the header (and the close button) were cut off, and a pin
 * in the lower half was shoved 500px up regardless of how tall the thread
 * actually was, so a two-line thread opened far above the pin it belonged to.
 *
 * This is a pure function so the rules are testable without a DOM: prefer the
 * pin's right, flip to its left when that overflows, keep the pin near the top
 * of the popover, and never let any edge leave the viewport. The caller passes
 * the popover's MEASURED height so a short thread stays beside its pin.
 */

/** Distance kept between the popover and every viewport edge. */
export const POPOVER_VIEWPORT_MARGIN = 16
/** Horizontal gap between the pin's centre and the popover's near edge. */
export const POPOVER_PIN_GAP = 50
/** How far above the pin's centre the popover's top sits when there is room. */
export const POPOVER_ANCHOR_OFFSET_Y = 100
export const POPOVER_WIDTH = 400
export const POPOVER_MAX_HEIGHT = 600

export interface ThreadPopoverPlacementInput {
  /** Pin centre, in viewport (client) coordinates. */
  anchorX: number
  anchorY: number
  viewportWidth: number
  viewportHeight: number
  /**
   * The popover's rendered height, if known. Before the first layout pass it is
   * unknown; the placement then reserves the full max height so the popover
   * cannot start off-screen and jump once measured.
   */
  measuredHeight?: number
  width?: number
}

export interface ThreadPopoverPlacement {
  left: number
  top: number
  /** The popover's rendered width — the preferred width, or the viewport's if narrower. */
  width: number
  maxHeight: number
}

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max)

export const placeThreadPopover = ({
  anchorX,
  anchorY,
  viewportWidth,
  viewportHeight,
  measuredHeight,
  width: preferredWidth = POPOVER_WIDTH,
}: ThreadPopoverPlacementInput): ThreadPopoverPlacement => {
  const margin = POPOVER_VIEWPORT_MARGIN

  // A viewport narrower than the popover (a phone, a split window) gets a
  // narrower popover, not one whose right edge — and close button — is off
  // screen (greptile on #34975).
  const width = Math.max(0, Math.min(preferredWidth, viewportWidth - margin * 2))

  // A short viewport cannot hold 600px; shrink so the thread scrolls inside the
  // popover instead of the popover running off the bottom.
  const maxHeight = Math.max(0, Math.min(POPOVER_MAX_HEIGHT, viewportHeight - margin * 2))
  const height =
    measuredHeight !== undefined && measuredHeight > 0 ? Math.min(measuredHeight, maxHeight) : maxHeight

  // Right of the pin by default; left of it when the right side is off-screen.
  let left = anchorX + POPOVER_PIN_GAP
  if (left + width + margin > viewportWidth) {
    left = anchorX - POPOVER_PIN_GAP - width
  }
  left = clamp(left, margin, Math.max(margin, viewportWidth - width - margin))

  // The pin sits near the top of the popover, but the popover never leaves the
  // viewport: a pin near the top edge pushes it down, a pin near the bottom
  // edge pulls it up — by exactly as much as its real height requires.
  const top = clamp(
    anchorY - POPOVER_ANCHOR_OFFSET_Y,
    margin,
    Math.max(margin, viewportHeight - height - margin),
  )

  return { left, top, width, maxHeight }
}
